import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { APIResponseType } from 'app/components/DataGridMAIQ/types';
import RunLogs, { RunLog } from 'app/apiService/runLogs/runLogsApiService';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import RunLogsSocketService, { RunLogEvent } from 'app/socketService/runLogs/runLogsSocketService';
import socketServiceManager from 'app/socketService';
import NotificationPersistenceService from 'app/services/NotificationPersistenceService';

type RunLogsContextType = {
  runLogs: APIResponseType;
  selectedRunLog: RunLog | null;
  loading: boolean;
  error: string | null;
  fetchRunLogs: (dealerId?: string) => Promise<void>;
  fetchRunByRunId: (runId: string, dealerId?: string) => Promise<void>;
  clearSelectedRunLog: () => void;
  // Socket-related properties
  socketConnected: boolean;
  realTimeRunLogs: RunLogEvent[];
  subscribeToRunLog: (runId: string) => void;
  unsubscribeFromRunLog: (runId: string) => void;
  testSocketConnection: () => void;
  clearPersistentData: () => void;
};

const RunLogsContext = createContext<RunLogsContextType>({
  runLogs: { 
    body: { 
      data: [], 
      headers: [],
      meta: { 
        totalDocuments: 0, 
        totalPages: 0, 
        currentPage: 1, 
        pageSize: 20,
        isOpportunity: false
      },
      gridType: 'segments'
    },
    statusCode: 200,
    statusText: 'OK',
    message: 'Success'
  },
  selectedRunLog: null,
  loading: false,
  error: null,
  fetchRunLogs: async () => {},
  fetchRunByRunId: async () => {},
  clearSelectedRunLog: () => {},
  // Socket-related default values
  socketConnected: false,
  realTimeRunLogs: [],
  subscribeToRunLog: () => {},
  unsubscribeFromRunLog: () => {},
  testSocketConnection: () => {},
  clearPersistentData: () => {}
});

export const useRunLogs = () => useContext(RunLogsContext);

export const RunLogsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [runLogs, setRunLogs] = useState<APIResponseType>({ 
    body: { 
      data: [], 
      headers: [],
      meta: { 
        totalDocuments: 0, 
        totalPages: 0, 
        currentPage: 1, 
        pageSize: 20,
        isOpportunity: false
      },
      gridType: 'segments'
    },
    statusCode: 200,
    statusText: 'OK',
    message: 'Success'
  });
  const [selectedRunLog, setSelectedRunLog] = useState<RunLog | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [realTimeRunLogs, setRealTimeRunLogs] = useState<RunLogEvent[]>([]);
  const selectedDealer = useSelector(selectDealerSelected);
  
  // Refs to prevent stale closures
  const socketServiceRef = useRef<RunLogsSocketService | null>(null);
  const isInitializedRef = useRef(false);
  const persistenceServiceRef = useRef<NotificationPersistenceService>(NotificationPersistenceService.getInstance());
  const restoredSubscriptionsRef = useRef<string[]>([]);

  // Initialize socket service
  useEffect(() => {
    if (isInitializedRef.current) return;
    
    const socketConfig = {
      url: process.env.REACT_APP_API_URL || 'http://localhost:3000',
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    };

    try {
      // Restore previous connection state
      const previousConnectionState = persistenceServiceRef.current.getConnectionState();
      
      // Initialize global socket connection
      socketServiceManager.initializeGlobalSocket(socketConfig);
      
      // Create and register RunLogs service
      const runLogsService = RunLogsSocketService.getInstance(socketConfig);
      socketServiceManager.registerService(runLogsService);
      socketServiceRef.current = runLogsService;
      
      // Set up event listeners with proper error handling
      runLogsService.on('run_log_event', (data: any) => {
        try {
          // Validate data structure
          if (!data || typeof data !== 'object') {
            return;
          }

          // Handle nested message structure from backend
          let eventData = data;
          if (data.message && typeof data.message === 'object') {
            // Backend sends data in nested message object
            eventData = data.message;
          }

          // Create a safe RunLogEvent object with fallback values
          const runLogEvent: RunLogEvent = {
            run_log_id: eventData.run_log_id || eventData.run_id || eventData.id || 'unknown',
            runId: eventData.run_id || eventData.run_log_id || eventData.id,
            id: eventData.id || eventData.run_id || eventData.run_log_id,
            status: eventData.status || 'unknown',
            message: eventData.message || 'No message available',
            error: eventData.error || undefined,
            start_timestamp: eventData.start_timestamp || eventData.created_at || new Date().toISOString(),
            end_timestamp: eventData.end_timestamp || eventData.updated_at || undefined,
            duration_ms: eventData.duration_ms || undefined,
            progress: eventData.progress || undefined,
            total_records: eventData.total_records || undefined,
            processed_records: eventData.processed_records || undefined,
            timestamp: new Date().toISOString(),
            created_at: eventData.created_at || eventData.start_timestamp,
            updated_at: eventData.updated_at || eventData.end_timestamp
          };
          
          // Update real-time run logs with deduplication
          setRealTimeRunLogs(prev => {
            const existingIndex = prev.findIndex(log => log.run_log_id === runLogEvent.run_log_id);
            if (existingIndex >= 0) {
              // Update existing entry
              const updated = [...prev];
              updated[existingIndex] = runLogEvent;
              return updated;
            } else {
              // Add new entry
              return [...prev, runLogEvent];
            }
          });
        } catch (error) {
        }
      });

      // Set up connection event listeners
      runLogsService.on('connect', () => {
        setSocketConnected(true);
        persistenceServiceRef.current.saveConnectionState(true);
        
        // Restore subscriptions after successful connection
        if (restoredSubscriptionsRef.current.length > 0) {
          runLogsService.restoreSubscriptions(restoredSubscriptionsRef.current);
        }
      });

      runLogsService.on('disconnect', (reason) => {
        setSocketConnected(false);
        persistenceServiceRef.current.saveConnectionState(false);
      });

      runLogsService.on('connect_error', (error) => {
        setSocketConnected(false);
        persistenceServiceRef.current.saveConnectionState(false);
      });

      runLogsService.on('reconnect', (attemptNumber) => {
        setSocketConnected(true);
        persistenceServiceRef.current.saveConnectionState(true);
        
        // Restore subscriptions after reconnection
        if (restoredSubscriptionsRef.current.length > 0) {
          runLogsService.restoreSubscriptions(restoredSubscriptionsRef.current);
        }
      });

      // Connect to socket
      runLogsService.connect();

      // Restore previous subscriptions
      const previousSubscriptions = persistenceServiceRef.current.getSubscriptionRunIds();
      if (previousSubscriptions.length > 0) {
        restoredSubscriptionsRef.current = previousSubscriptions;
      }

      isInitializedRef.current = true;
    } catch (error) {
      setError('Failed to initialize socket service');
    }

    // Cleanup function
    return () => {
      try {
        if (socketServiceRef.current) {
          socketServiceRef.current.disconnect();
        }
        // Don't disconnect all services - let other services continue using the global socket
        // socketServiceManager.disconnectAll();
      } catch (error) {
      }
    };
  }, []);

  // Check connection status periodically
  useEffect(() => {
    if (!socketServiceRef.current) return;

    const interval = setInterval(() => {
      try {
        const isConnected = socketServiceRef.current?.isConnected() || false;
        setSocketConnected(isConnected);
        persistenceServiceRef.current.saveConnectionState(isConnected);
        
      } catch (error) {
        setSocketConnected(false);
        persistenceServiceRef.current.saveConnectionState(false);
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        persistenceServiceRef.current.updateLastActivity();
        
        // Reconnect if needed and restore subscriptions
        if (socketServiceRef.current && !socketServiceRef.current.isConnected()) {
          socketServiceRef.current.connect();
          
          // Restore subscriptions after a short delay
          setTimeout(() => {
            const subscriptions = persistenceServiceRef.current.getSubscriptionRunIds();
            if (subscriptions.length > 0) {
              socketServiceRef.current?.restoreSubscriptions(subscriptions);
            }
          }, 1000);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const fetchRunLogs = async (dealerId?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const dealerToUse = dealerId || selectedDealer?._id;
      if (!dealerToUse) {
        throw new Error('No dealer selected');
      }

      const response = await RunLogs.getAllRunLogs({
        dealer: dealerToUse,
        skip: 0,
        limit: 20
      });

      // Validate response structure
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response from server');
      }

      setRunLogs(response as APIResponseType);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while fetching run logs';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchRunByRunId = async (runId: string, dealerId?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!runId) {
        throw new Error('Run ID is required');
      }

      const response = await RunLogs.getRunByRunId(runId);

      // Validate response structure
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response from server');
      }

      // Validate response.body before setting it
      if (response.body) {
        // Check if response.body has the expected structure
        if (typeof response.body === 'object' && response.body !== null) {
          // Ensure it has required fields for RunLog interface with safe fallbacks
          const validatedRunLog: RunLog = {
            _id: response.body._id || response.body.run_id || response.body.id || 'unknown',
            name: response.body.name || response.body.run_name || 'Unknown Run',
            initial_parameters: response.body.initial_parameters || {},
            dealer_id: response.body.dealer_id || response.body.dealer || 'unknown',
            status: response.body.status || 'unknown',
            run_version: response.body.run_version || '1.0',
            run_type: response.body.run_type || response.body.type || 'unknown',
            run_by: response.body.run_by || response.body.created_by || 'unknown',
            run_by_id: response.body.run_by_id || response.body.created_by_id || 'unknown',
            created_at: response.body.created_at || response.body.start_timestamp || new Date().toISOString(),
            updated_at: response.body.updated_at || response.body.end_timestamp || new Date().toISOString(),
            actions: Array.isArray(response.body.actions) ? response.body.actions : []
          };
          
          setSelectedRunLog(validatedRunLog);
        } else {
          throw new Error('Invalid run log data structure received from server');
        }
      } else {
        throw new Error('No run log data received from server');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while fetching run details';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const clearSelectedRunLog = () => {
    setSelectedRunLog(null);
  };

  // Socket-related methods with better error handling
  const subscribeToRunLog = useCallback((runId: string) => {
    try {
      if (!runId || typeof runId !== 'string') {
        return;
      }

      const runLogsService = socketServiceRef.current || socketServiceManager.getService<RunLogsSocketService>('RunLogsSocketService');
      if (runLogsService) {
        console.log(`📡 Subscribing to run log: ${runId}`);
        runLogsService.subscribeToRunLog(runId);
        
        // Persist subscription
        persistenceServiceRef.current.addSubscription(runId, selectedDealer?._id);
        
        // Update restored subscriptions ref
        if (!restoredSubscriptionsRef.current.includes(runId)) {
          restoredSubscriptionsRef.current.push(runId);
        }
      } else {
      }
    } catch (error) {
    }
  }, [selectedDealer?._id]);

  const unsubscribeFromRunLog = useCallback((runId: string) => {
    try {
      if (!runId || typeof runId !== 'string') {
        return;
      }

      const runLogsService = socketServiceRef.current || socketServiceManager.getService<RunLogsSocketService>('RunLogsSocketService');
      if (runLogsService) {
        runLogsService.unsubscribeFromRunLog(runId);
        
        // Remove from persistence
        persistenceServiceRef.current.removeSubscription(runId);
        
        // Update restored subscriptions ref
        restoredSubscriptionsRef.current = restoredSubscriptionsRef.current.filter(id => id !== runId);
      } else {
      }
    } catch (error) {
    }
  }, []);

  // Test socket connection
  const testSocketConnection = useCallback(() => {
    try {
      const runLogsService = socketServiceRef.current || socketServiceManager.getService<RunLogsSocketService>('RunLogsSocketService');
      if (runLogsService) {
        
        
        // Test emit a simple event
        runLogsService.emit('test_connection', { message: 'Testing connection from frontend' });
      } else {
      }
    } catch (error) {
    }
  }, []);

  // Clear all persistent data
  const clearPersistentData = useCallback(() => {
    try {
      persistenceServiceRef.current.clearAllData();
      restoredSubscriptionsRef.current = [];
    } catch (error) {
    }
  }, []);

  return (
    <RunLogsContext.Provider value={{ 
      runLogs, 
      selectedRunLog,
      loading, 
      error, 
      fetchRunLogs,
      fetchRunByRunId,
      clearSelectedRunLog,
      // Socket-related values
      socketConnected,
      realTimeRunLogs,
      subscribeToRunLog,
      unsubscribeFromRunLog,
      testSocketConnection,
      clearPersistentData
    }}>
      {children}
    </RunLogsContext.Provider>
  );
};
